import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useGameBase from '../../hooks/useGameBase';
import getServerBaseUrl from '../../utils/serverBaseUrl';

function makeCircleIcon(bgColor, label) {
  return L.divIcon({
    className: '',
    html: `<div style="width:26px;height:26px;border-radius:50%;background:${bgColor};border:2px solid white;display:flex;align-items:center;justify-content:center;color:white;font-size:11px;font-weight:bold;box-shadow:0 2px 5px rgba(0,0,0,.45);">${label}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

function makeActualIcon() {
  return L.divIcon({
    className: '',
    html: `<div style="font-size:28px;line-height:1;filter:drop-shadow(0 2px 3px rgba(0,0,0,.6));">X</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 26],
  });
}

const PLAYER_COLORS = ['#4f46e5', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#be185d', '#92400e'];

const SERVER_BASE = getServerBaseUrl();

function buildMapillaryEmbedUrl(imageId, style = 'photo') {
  return `https://www.mapillary.com/embed?image_key=${encodeURIComponent(imageId)}&style=${encodeURIComponent(style || 'photo')}`;
}

function MapillaryEmbed({ imageId, style = 'photo', title }) {
  const embedUrl = useMemo(() => buildMapillaryEmbedUrl(imageId, style), [imageId, style]);

  return (
    <iframe
      title={title || 'Mapillary street view'}
      src={embedUrl}
      className="h-full w-full border-0"
      allowFullScreen
    />
  );
}


function MapClickHandler({ onMapClick, enabled }) {
  useMapEvents({ click(e) { if (enabled) onMapClick(e.latlng); } });
  return null;
}

function MapController({ phase, round, revealBounds, focusTarget }) {
  const map = useMap();

  // Reset to world view on new guessing round
  useEffect(() => {
    if (phase === 'guessing') map.setView([20, 0], 1);
  }, [phase, round, map]);

  // Fit bounds when reveal starts
  useEffect(() => {
    if (phase === 'reveal' && revealBounds && revealBounds.length > 0) {
      const t = setTimeout(() => {
        map.invalidateSize();
        if (revealBounds.length === 1) {
          map.setView(revealBounds[0], 6);
        } else {
          map.fitBounds(L.latLngBounds(revealBounds), { padding: [60, 60] });
        }
      }, 100);
      return () => clearTimeout(t);
    }
  }, [phase, round, revealBounds, map]);

  useEffect(() => {
    if (phase !== 'guessing' || !focusTarget) return;
    const { lat, lng, zoom } = focusTarget;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    map.setView([lat, lng], Number.isFinite(zoom) ? zoom : Math.max(map.getZoom(), 6));
  }, [focusTarget, phase, map]);

  return null;
}


function TimerBar({ startTime, timeLimit, active, color = 'bg-yellow-400' }) {
  const [timeLeft, setTimeLeft] = useState(timeLimit || 0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (!active || !startTime || !timeLimit) {
      setTimeLeft(timeLimit || 0);
      return;
    }
    const tick = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const rem = Math.max(0, Math.ceil(timeLimit - elapsed));
      setTimeLeft(rem);
      if (elapsed < timeLimit) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [active, startTime, timeLimit]);

  const pct = timeLimit ? Math.min(1 - timeLeft / timeLimit, 1) : 0;

  return (
    <div className="w-full mt-2 mb-1">
      <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
        <div className={`h-full ${color} transition-none`} style={{ width: `${pct * 100}%` }} />
      </div>
      <div className="text-xs text-right text-gray-500 dark:text-gray-400 mt-0.5">{timeLeft}s left</div>
    </div>
  );
}


export default function WhereAmIGame({ roomId, isHost, gameState, gamePlayers = [], user = {}, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, roomId });

  const [pendingGuess, setPendingGuess] = useState(null); // { lat, lng }
  const [guessSubmitted, setGuessSubmitted] = useState(false);
  const [resolvedMapillary, setResolvedMapillary] = useState(null);
  const [mapFocusTarget, setMapFocusTarget] = useState(null);
  const [placeSearch, setPlaceSearch] = useState({ query: '', results: [], searching: false, error: '' });

  const phase = gameState ? gameState.phase : 'guessing';
  const round = (gameState && gameState.round) || 0;
  const rounds = (gameState && gameState.rounds) || 5;
  const displayRound = phase === 'finished' ? round : Math.min(rounds, Math.max(1, round));
  const panorama = gameState && gameState.currentPanorama;
  const guessedPlayers = gameState ? (gameState.guessedPlayers || []) : [];
  const players = gameState ? (gameState.players || []) : [];
  const scores = gameState ? (gameState.scores || {}) : {};
  const results = gameState ? (gameState.results || []) : [];
  const lastResult = results.length > 0 ? results[results.length - 1] : null;
  const mySid = user && user.socketId;
  const hasPanorama = !!panorama;
  const panoramaLat = panorama?.lat;
  const panoramaLng = panorama?.lng;
  const panoramaMapillaryImageId = panorama?.mapillaryImageId;
  const panoramaMapillaryStyle = panorama?.mapillaryStyle;
  const panoramaMapillaryThumbUrl = panorama?.mapillaryThumbUrl;

  // Reset per-round state
  useEffect(() => {
    if (phase === 'guessing') {
      setPendingGuess(null);
      setGuessSubmitted(false);
      setResolvedMapillary(null);
      setMapFocusTarget(null);
      setPlaceSearch({ query: '', results: [], searching: false, error: '' });
    }
  }, [phase, round]);

  useEffect(() => {
    if (!hasPanorama) {
      setResolvedMapillary(null);
      return undefined;
    }

    if (panoramaMapillaryImageId) {
      setResolvedMapillary({
        id: panoramaMapillaryImageId,
        style: panoramaMapillaryStyle || 'photo',
        thumbUrl: panoramaMapillaryThumbUrl || null,
      });
      return undefined;
    }

    if (!Number.isFinite(panoramaLat) || !Number.isFinite(panoramaLng)) {
      setResolvedMapillary(null);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    fetch(`${SERVER_BASE}/api/whereami/mapillary/search?lat=${encodeURIComponent(panoramaLat)}&lng=${encodeURIComponent(panoramaLng)}&limit=1`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok || !payload?.ok) {
          throw new Error(payload?.error || 'Mapillary lookup failed');
        }
        if (!cancelled) {
          const match = Array.isArray(payload.images) ? payload.images[0] : null;
          setResolvedMapillary(match ? {
            id: match.id,
            style: panoramaMapillaryStyle || 'photo',
            thumbUrl: match.thumbUrl || null,
          } : null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedMapillary(null);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [hasPanorama, panoramaLat, panoramaLng, panoramaMapillaryImageId, panoramaMapillaryStyle, panoramaMapillaryThumbUrl, round]);

  const dispatch = useCallback(async (action) => {
    if (typeof onGameAction === 'function') onGameAction(action);
    else await submitAction(action);
  }, [onGameAction, submitAction]);

  const handleMapClick = useCallback((latlng) => {
    if (guessSubmitted || phase !== 'guessing') return;
    setPendingGuess({ lat: latlng.lat, lng: latlng.lng });
  }, [guessSubmitted, phase]);

  const handlePlaceSearch = useCallback(async (event) => {
    event.preventDefault();
    const query = placeSearch.query.trim();

    if (!query) {
      setPlaceSearch((current) => ({ ...current, results: [], error: 'Enter a place to search.' }));
      return;
    }

    setPlaceSearch((current) => ({ ...current, searching: true, results: [], error: '' }));
    try {
      const response = await fetch(`${SERVER_BASE}/api/whereami/geocode?q=${encodeURIComponent(query)}&limit=6`);
      const payload = await response.json();
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || 'Place search failed');
      }

      const results = Array.isArray(payload.results) ? payload.results : [];
      setPlaceSearch((current) => ({
        ...current,
        searching: false,
        results,
        error: results.length ? '' : 'No places matched that search.',
      }));
    } catch (error) {
      setPlaceSearch((current) => ({
        ...current,
        searching: false,
        results: [],
        error: String(error.message || error),
      }));
    }
  }, [placeSearch.query]);

  const handlePlaceSelect = useCallback((result) => {
    if (!result || guessSubmitted || phase !== 'guessing') return;

    setPendingGuess({ lat: result.lat, lng: result.lng });
    setMapFocusTarget({ lat: result.lat, lng: result.lng, zoom: 7, key: `${result.lat}:${result.lng}:${Date.now()}` });
    setPlaceSearch((current) => ({ ...current, query: result.name, results: [], error: '' }));
  }, [guessSubmitted, phase]);

  const handleSubmitGuess = useCallback(async () => {
    if (!pendingGuess || guessSubmitted) return;
    setGuessSubmitted(true);
    await dispatch({ type: 'submitGuess', lat: pendingGuess.lat, lng: pendingGuess.lng });
  }, [pendingGuess, guessSubmitted, dispatch]);

  function fmtDist(km) {
    if (km == null) return 'No guess';
    if (km < 1) return '< 1 km';
    return `${km.toLocaleString()} km`;
  }

  // Build bounds array for reveal phase
  const revealBounds = useMemo(() => {
    if (!lastResult) return null;
    const pts = [];
    if (lastResult.location) pts.push([lastResult.location.lat, lastResult.location.lng]);
    (lastResult.perPlayer || []).forEach(p => { if (p.guess) pts.push([p.guess.lat, p.guess.lng]); });
    return pts.length > 0 ? pts : null;
  }, [lastResult]);

  const mapillaryMode = !!resolvedMapillary?.id;

  
  if (phase === 'finished') {
    const result = gameState && gameState.result;
    const sortedPlayers = [...players].sort((a, b) => (scores[b.socketId] || 0) - (scores[a.socketId] || 0));
    return (
      <div className="flex flex-col items-center min-h-full bg-gray-50 dark:bg-gray-950 p-4">
        <div className="w-full max-w-lg">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow p-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Game Over!</h2>
            {result && result.winnerNames && (
              <div className="text-indigo-600 dark:text-indigo-400 font-semibold mb-4">
                {result.outcome === 'tie' ? "It's a tie " : 'Winner: '}{result.winnerNames}
              </div>
            )}
            <div className="space-y-2 mt-4">
              {sortedPlayers.map((p, i) => {
                const isMe = p.socketId === mySid;
                return (
                  <div key={p.socketId}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
                      isMe ? 'bg-indigo-50 dark:bg-indigo-950 border-indigo-300' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-400 w-6">#{i + 1}</span>
                      <span className="font-medium text-gray-800 dark:text-gray-200">{p.username || p.socketId}</span>
                      {isMe && <span className="text-xs text-indigo-500">(you)</span>}
                    </div>
                    <span className="font-bold text-gray-800 dark:text-gray-200">{(scores[p.socketId] || 0).toLocaleString()} pts</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="where-am-i-game flex flex-col items-center min-h-full bg-gray-50 dark:bg-gray-950 p-2 pb-4">
      <div className="w-full max-w-2xl">

        {/* Header */}
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="text-sm text-gray-500 dark:text-gray-400">Round {displayRound} of {rounds}</div>
          
        </div>

        {/* Street view panel */}
        <div className="bg-gray-800 rounded-xl overflow-hidden shadow mb-2 relative" style={{ height: '220px' }}>
          {mapillaryMode ? (
            <MapillaryEmbed
              imageId={resolvedMapillary.id}
              style={resolvedMapillary.style || 'photo'}
              title={panorama?.name || 'Mapillary street view'}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 text-sm gap-2">
              <span>No Mapillary street view is available for this round.</span>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-2 rounded-lg bg-sky-600 hover:bg-sky-700 px-4 py-2 text-white font-semibold shadow"
              >
                Reload Page
              </button>
            </div>
          )}
        </div>

        {phase === 'guessing' && (
          <div className="mb-2 rounded-xl bg-white p-4 shadow dark:bg-gray-900">
            <TimerBar startTime={gameState && gameState.startTime} timeLimit={gameState && gameState.timeLimit} active color="bg-indigo-400" />
            
            <form className="mt-3 flex gap-2" onSubmit={handlePlaceSearch}>
              <input
                value={placeSearch.query}
                onChange={(event) => setPlaceSearch((current) => ({ ...current, query: event.target.value, error: '' }))}
                placeholder="Search for a country, city, landmark, or address"
                className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              />
              <button
                type="submit"
                disabled={placeSearch.searching}
                className={`rounded-lg px-3 py-2 text-sm font-semibold text-white ${placeSearch.searching ? 'bg-gray-400' : 'bg-sky-600 hover:bg-sky-700'}`}
              >
                {placeSearch.searching ? 'Searching...' : 'Find Place'}
              </button>
            </form>
            {placeSearch.error && (
              <div className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
                {placeSearch.error}
              </div>
            )}
            {placeSearch.results.length > 0 && (
              <div className="mt-2 grid gap-2">
                {placeSearch.results.map((result) => (
                  <button
                    key={`${result.name}-${result.lat}-${result.lng}`}
                    type="button"
                    onClick={() => handlePlaceSelect(result)}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left text-sm text-gray-800 hover:border-sky-300 hover:bg-sky-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-sky-700 dark:hover:bg-gray-750"
                  >
                    {result.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Map panel  always visible */}
        <div className="rounded-xl overflow-hidden shadow mb-2" style={{ height: '250px' }}>
          <MapContainer
            center={[20, 0]}
            zoom={1}
            style={{ height: '100%', width: '100%' }}
            zoomControl
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            />
            <MapController phase={phase} round={round} revealBounds={revealBounds} focusTarget={mapFocusTarget} />
            <MapClickHandler onMapClick={handleMapClick} enabled={phase === 'guessing' && !guessSubmitted} />

            {/* Guess pin */}
            {phase === 'guessing' && pendingGuess && (
              <Marker position={[pendingGuess.lat, pendingGuess.lng]} icon={makeCircleIcon('#4f46e5', '?')} />
            )}

            {/* Reveal: actual location + player guesses + lines */}
            {phase === 'reveal' && lastResult && (
              <>
                <Marker
                  position={[lastResult.location.lat, lastResult.location.lng]}
                  icon={makeActualIcon()}
                />
                {(lastResult.perPlayer || []).map((p, i) => {
                  if (!p.guess) return null;
                  const color = PLAYER_COLORS[i % PLAYER_COLORS.length];
                  return (
                    <React.Fragment key={p.socketId}>
                      <Marker position={[p.guess.lat, p.guess.lng]} icon={makeCircleIcon(color, String(i + 1))} />
                      <Polyline
                        positions={[[p.guess.lat, p.guess.lng], [lastResult.location.lat, lastResult.location.lng]]}
                        pathOptions={{ color, weight: 2, opacity: 0.65, dashArray: '7 6' }}
                      />
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </MapContainer>
        </div>

        {phase === 'guessing' && (
          <div className="mb-3">
            <button
              onClick={handleSubmitGuess}
              disabled={!pendingGuess || guessSubmitted}
              className={`w-full py-3 rounded-lg text-sm font-bold transition-colors ${
                pendingGuess && !guessSubmitted
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              {guessSubmitted ? 'Guess Submitted' : 'Submit Guess'}
            </button>
          </div>
        )}

        {/* ── GUESSING phase UI ──────────────────────────────────────────────────────────────── */}

        {phase === 'guessing' && (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow p-4">
            <div className="flex flex-wrap gap-2">
              {players.map(p => {
                const guessed = guessedPlayers.includes(p.socketId) || (p.socketId === mySid && guessSubmitted);
                return (
                  <span key={p.socketId}
                    className={`text-xs px-2.5 py-1 rounded-full border font-medium ${
                      guessed
                        ? 'bg-green-100 dark:bg-green-900 border-green-400 text-green-700 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {guessed ? '✓' : '⏳'} {p.username || p.socketId}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {}
        {phase === 'reveal' && lastResult && (
          <div className="mt-3 bg-white dark:bg-gray-900 rounded-xl shadow p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg"></span>
              <span className="font-bold text-gray-900 dark:text-gray-100">{lastResult.location.name}</span>
            </div>
            <TimerBar startTime={gameState && gameState.startTime} timeLimit={gameState && gameState.timeLimit} active color="bg-gray-400" />
            <div className="space-y-2 mt-3">
              {(lastResult.perPlayer || []).map((p, i) => {
                const isMe = p.socketId === mySid;
                const colorClasses = ['text-indigo-600', 'text-green-600', 'text-amber-600', 'text-rose-600', 'text-violet-600', 'text-cyan-600', 'text-pink-600', 'text-orange-600'];
                return (
                  <div key={p.socketId}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                      isMe ? 'bg-indigo-50 dark:bg-indigo-950 border-indigo-300' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`font-bold text-sm w-5 ${colorClasses[i % colorClasses.length]} dark:opacity-80`}>#{i + 1}</span>
                      <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                        {p.username || p.socketId}
                        {isMe && <span className="ml-1 text-xs text-indigo-500">(you)</span>}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-800 dark:text-gray-200">+{(p.pointsEarned || 0).toLocaleString()} pts</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{fmtDist(p.distanceKm)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Total Scores</div>
              <div className="flex flex-wrap gap-2">
                {[...players].sort((a, b) => (scores[b.socketId] || 0) - (scores[a.socketId] || 0)).map(p => (
                  <span key={p.socketId}
                    className={`text-xs px-2.5 py-1 rounded-full border ${
                      p.socketId === mySid ? 'bg-indigo-100 dark:bg-indigo-900 border-indigo-300 font-semibold text-indigo-700 dark:text-indigo-300' : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {p.username || p.socketId}: {(scores[p.socketId] || 0).toLocaleString()}
                  </span>
                ))}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center">Next round starting soon…</div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

WhereAmIGame.Guide = function WhereAmIGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2 text-gray-900 dark:text-gray-100">Where Am I?</h3>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 dark:text-gray-300">
        <li>Each round shows embedded Mapillary street-level imagery.</li>
        <li>Drag within the Mapillary view to look around before guessing.</li>
        <li>Click anywhere on the map to <strong>drop your guess pin</strong>.</li>
        <li>Press <strong>Submit Guess</strong> before time runs out.</li>
        <li>Points are based on how close your guess is — the closer, the more points!</li>
        <li>After all rounds, the player with the most points wins.</li>
      </ul>
    </div>
  );
};

